import { workflowExecutionEnvironmentSchema, workflowExecutionSnapshotSchema, workflowMemoSchema } from "./workflow-execution.js";
import { z } from "zod";
import { workflowNodeTraceSchema } from "./workflow-trace.js";
import { durableWorkflowDefinitionSchema } from "./workflow-resources.js";
import { workflowRunCheckpointSchema } from "./workflow-storage.js";
import { twinWorkflowReviewPayloadSchema, workflowReviewAskSchema, workflowReviewDraftFindingSchema } from "./twin-proposals.js";
import { workflowPluginSourceAuthoritySchema } from "./workflow-plugin-sources.js";
/** A workflow drafts findings; what a judge answered is resolved by the server, so a draft cannot carry it. */
export const workflowReviewDraftSchema = twinWorkflowReviewPayloadSchema.pick({
    title: true, summary: true, postingIntent: true, pair: true,
}).extend({
    /** What the card can ask Twin to do that the workflow can't. The person sends it, so it is never `sent` yet. */
    ask: workflowReviewAskSchema.omit({ sent: true }).optional(),
    /**
     * The record's own name, such as "Modal.com RBHJUUC-0019", where the item's title does not name it (an uploaded
     * document's title is its file name). Not the ask, which is `title`. Blank is absent: the engine then names the
     * card from the item's title.
     */
    subject: z.preprocess((value) => typeof value === "string" && !value.trim() ? undefined : value, z.string().trim().min(1).max(300).optional()),
    /**
     * The values Accept writes to a record the run read (`evidenceRef`, a `ctx_…` read of it), within the review step's
     * declared `recordWrites`. The engine resolves the record and what each field holds now.
     */
    recordWrite: z.object({ evidenceRef: z.string().regex(/^ctx_[0-9a-f]{40}$/u),
        values: z.record(z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/u), z.json())
            .refine((values) => Object.keys(values).length >= 1 && Object.keys(values).length <= 8, "A card writes one to eight fields") }).strict().optional(),
    findings: z.array(workflowReviewDraftFindingSchema).min(1).max(100),
}).strict().superRefine((draft, ctx) => {
    // A side of the pair names evidence only the card itself cites, so it opens what the reviewer can read.
    const cited = new Set(draft.findings.flatMap((finding) => finding.evidenceRefs));
    for (const side of ["left", "right"]) {
        const ref = draft.pair?.[side].evidenceRef;
        if (ref && !cited.has(ref))
            ctx.addIssue({ code: "custom", path: ["pair", side, "evidenceRef"], message: "A pair names evidence its findings cite" });
    }
    // An Ask's facts are the card's first finding's: each mark names one of its record fields.
    const fields = draft.findings[0]?.recordFields?.length ?? 0;
    for (const part of ["prompt", "draft"])
        draft.ask?.[part]?.forEach((segment, index) => {
            if (segment.mark && segment.mark > fields)
                ctx.addIssue({ code: "custom", path: ["ask", part, index, "mark"], message: "An Ask's mark names a record field of the card's first finding" });
        });
});
const runIdentity = {
    tenantSlug: z.string().min(1), runId: z.string().min(1),
    workerId: z.string().min(1), attemptNumber: z.number().int().positive(),
};
const identity = { ...runIdentity, itemId: z.string().min(1) };
const sourceScalar = z.union([z.string().max(500), z.number().finite(), z.boolean()]);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u);
export const workflowSourceQuerySchema = z.object({
    after: z.string().min(1).max(4000).optional(),
    where: z.array(z.discriminatedUnion("op", [
        z.object({ fieldKey: z.string().min(1), op: z.literal("eq"), value: sourceScalar }).strict(),
        z.object({ fieldKey: z.string().min(1), op: z.literal("in"),
            values: z.array(sourceScalar).min(1).max(20) }).strict(),
        z.object({ fieldKey: z.string().min(1), op: z.literal("alphanumeric_eq"),
            value: z.string().min(1).max(200) }).strict(),
        z.object({ fieldKey: z.string().min(1), op: z.literal("numeric_eq"),
            value: z.string().regex(/^-?\d{1,20}(?:\.\d{1,6})?$/u) }).strict(),
        z.object({ fieldKey: z.string().min(1), op: z.literal("date_between"),
            from: isoDate, to: isoDate }).strict().refine((range) => range.from <= range.to, "Date range must be ordered"),
    ])).min(1).max(4),
    select: z.array(z.string().min(1)).min(1).max(30),
    orderBy: z.array(z.object({ fieldKey: z.string().min(1),
        direction: z.enum(["asc", "desc"]) }).strict()).max(1).optional(),
    limit: z.number().int().min(1).max(25).default(25),
    /**
     * The count of every row `where` matches, and sums of up to five numeric fields over them, whatever page this is
     * (docs/specs/workflow-source-totals.md): a figure without reading every row.
     */
    totals: z.object({ sum: z.array(z.string().min(1)).max(5).default([]) }).strict().optional(),
}).strict();
/** Totals over every row a read's filter matches; sums are decimal strings. */
export const workflowSourceTotalsSchema = z.object({
    count: z.number().int().nonnegative(),
    sums: z.record(z.string(), z.string().regex(/^-?\d+(?:\.\d+)?$/u)),
}).strict();
export const workerWorkflowItemCommandRequestSchema = z.discriminatedUnion("command", [
    z.object({ ...identity, command: z.literal("source_auth"), sourceKey: z.string().min(1),
        nodeId: z.string().min(1), effectKey: z.string().min(1) }).strict(),
    z.object({ ...identity, command: z.literal("trace"), trace: workflowNodeTraceSchema }).strict(),
    z.object({ ...runIdentity, command: z.literal("list") }).strict(),
    z.object({ ...identity, command: z.literal("start"), environment: workflowExecutionEnvironmentSchema }).strict(),
    z.object({ ...identity, command: z.literal("memo"), memo: workflowMemoSchema }).strict(),
    z.object({ ...identity, command: z.literal("post"), nodeId: z.string().min(1), effectKey: z.string().min(1), proposalId: z.string().min(1) }).strict(),
    z.object({ ...identity, command: z.literal("review"), nodeId: z.string().min(1),
        effectKey: z.string().min(1), proposalId: z.string().min(1) }).strict(),
    z.object({ ...identity, command: z.literal("history"), nodeId: z.string().min(1),
        effectKey: z.string().min(1), value: z.string().min(1).max(500) }).strict(),
    z.object({ ...identity, command: z.literal("source"), sourceKey: z.string().min(1),
        nodeId: z.string().min(1), effectKey: z.string().min(1),
        query: workflowSourceQuerySchema }).strict(),
    z.object({ ...identity, command: z.literal("upsert"),
        nodeId: z.string().min(1), effectKey: z.string().min(1),
        values: z.record(z.string().min(1), z.json()).refine((value) => Object.keys(value).length > 0 && Object.keys(value).length <= 100) }).strict(),
    z.object({ ...identity, command: z.literal("proposal"),
        nodeId: z.string().min(1), effectKey: z.string().min(1),
        draft: workflowReviewDraftSchema }).strict(),
    z.object({ ...identity, command: z.literal("finish"),
        outcome: z.enum(["succeeded", "failed"]),
        errorCode: z.string().min(1).max(120).optional(),
        errorDetail: z.string().min(1).max(4000).optional() }).strict(),
]);
/** The item a workflow run starts on, as its nodes receive it. */
export const workerWorkflowItemSchema = z.object({ id: z.string().min(1), title: z.string().nullable(),
    values: z.record(z.string(), z.json()),
    duplicateCandidateItemIds: z.array(z.string().min(1)).max(20),
    duplicateCandidateOverflow: z.boolean(),
    sourceRefs: z.array(z.object({ sourceBindingId: z.string().min(1),
        sourceKey: z.string().min(1), adapterKind: z.string().min(1),
        externalId: z.string().min(1), subrecordKey: z.string().nullable(),
        sourceRevision: z.string().nullable(),
        documentFingerprint: z.string().nullable(),
        relationship: z.enum(["root", "related", "evidence"]),
        // An uploaded file's name and size, so a workflow can recognise a document before reading it. Accepted
        // before any tenant sends it, because the tenant API moves before the executor in a release.
        file: z.object({ name: z.string().min(1).max(500), byteSize: z.number().int().nonnegative() })
            .strict().optional() }).strict()).max(200) }).strict();
export const workerWorkflowItemCommandResponseSchema = z.discriminatedUnion("command", [
    z.object({ command: z.literal("source_auth"), source: workflowPluginSourceAuthoritySchema }).strict(),
    z.object({ command: z.literal("trace"), replayed: z.boolean() }).strict(),
    z.object({ command: z.literal("list"), activationId: z.string().min(1),
        workflowId: z.string().min(1), items: z.array(z.object({ itemId: z.string().min(1),
            inputRevision: z.string().min(1),
            status: z.enum(["pending", "running", "succeeded", "failed", "retry", "skipped"]) }).strict()).max(100) }).strict(),
    z.object({ command: z.literal("memo"), found: z.boolean(), value: z.json(),
        evidenceId: z.string().nullable() }).strict(),
    z.object({ command: z.literal("start"), started: z.boolean(), execution: workflowExecutionSnapshotSchema,
        definition: durableWorkflowDefinitionSchema,
        checkpoint: workflowRunCheckpointSchema, inputRevision: z.string().min(1),
        item: workerWorkflowItemSchema }).strict(),
    /** A start whose item changed after the run was queued and that a newer run holds: this run skipped it. */
    z.object({ command: z.literal("superseded"), detail: z.string().min(1).max(500) }).strict(),
    z.object({ command: z.literal("post"), intentId: z.string(), status: z.enum(["queued", "dispatching", "needs_reconciliation", "posted", "rejected", "blocked", "superseded"]), receipt: z.json().nullable() }).strict(),
    z.object({ command: z.literal("review"), status: z.enum(["pending", "accepted", "declined", "expired"]), decisionId: z.string().optional() }).strict(),
    z.object({ command: z.literal("history"), records: z.array(z.object({
            evidenceRef: z.string(), proposalId: z.string(), itemId: z.string(), decisionId: z.string(),
            decision: z.string(), note: z.string().nullable(), decidedAt: z.number(),
            findings: twinWorkflowReviewPayloadSchema.shape.findings,
        }).strict()).max(20), hasMore: z.boolean() }).strict(),
    z.object({ command: z.literal("source"), records: z.array(z.record(z.string(), z.json())).max(25),
        evidenceRefs: z.array(z.string().regex(/^ctx_[0-9a-f]{40}$/u)).max(25),
        nextCursor: z.string().min(1).max(4000).optional(),
        hasMore: z.boolean(), totals: workflowSourceTotalsSchema.optional() }).strict(),
    z.object({ command: z.literal("upsert"), replayed: z.boolean(),
        output: z.object({ recordId: z.string().min(1), version: z.number().int().positive() }).strict() }).strict(),
    z.object({ command: z.literal("proposal"), replayed: z.boolean(),
        output: z.object({ proposalId: z.string().min(1),
            observationId: z.string().min(1) }).strict() }).strict(),
    z.object({ command: z.literal("finish"), replayed: z.boolean(),
        status: z.enum(["succeeded", "failed"]) }).strict(),
]);
